import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, FileText, Building2, TrendingUp } from 'lucide-react';
import type { RevenueSummary } from '../types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(value);

interface RevenueSummaryCardsProps {
  data?: RevenueSummary;
  loading: boolean;
}

export function RevenueSummaryCards({ data, loading }: RevenueSummaryCardsProps) {
  const avgRevenue = data && data.total_accounts > 0 ? data.total_revenue / data.total_accounts : 0;

  const cards = [
    {
      title: 'Total Revenue',
      value: data ? formatCurrency(data.total_revenue) : '-',
      icon: DollarSign,
      description: 'All accounts combined',
    },
    {
      title: 'Total Invoices',
      value: data ? data.total_invoices.toLocaleString() : '-',
      icon: FileText,
      description: 'Across all accounts',
    },
    {
      title: 'Total Accounts',
      value: data ? data.total_accounts.toLocaleString() : '-',
      icon: Building2,
      description: 'Revenue-generating accounts',
    },
    {
      title: 'Avg Revenue / Account',
      value: data ? formatCurrency(avgRevenue) : '-',
      icon: TrendingUp,
      description: 'Average per account',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-8 w-24 animate-pulse rounded bg-muted" />
            ) : (
              <>
                <div className="text-2xl font-bold">{card.value}</div>
                <p className="text-xs text-muted-foreground">{card.description}</p>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
